import React from 'react';
import { Box } from '@gluestack-ui/themed';
import type { ComponentProps } from 'react';

type BoxProps = ComponentProps<typeof Box>;

export type ContentGroupVariant = 'accent' | 'bordered' | 'subtle' | 'flush';

export interface ContentGroupProps extends Omit<BoxProps, 'children'> {
  /** Content to display inside the group */
  children: React.ReactNode;
  /** Visual variant for the group */
  variant?: ContentGroupVariant;
  /** Color for the accent bar (only applies to 'accent' and 'bordered' variants) */
  accentColor?: string;
  /** Width of the accent bar in pixels (only applies to 'accent' and 'bordered' variants) */
  accentWidth?: number;
}

/**
 * ContentGroup - Standard component for visually grouping related content.
 * Replaces card-based organization with cleaner accent/border patterns.
 *
 * @example
 * // Accent variant (left bar only)
 * <ContentGroup variant="accent">
 *   <VStack space="md">...</VStack>
 * </ContentGroup>
 *
 * @example
 * // Bordered variant (full border + accent)
 * <ContentGroup variant="bordered" bg="$bgCard">
 *   <VStack space="md">...</VStack>
 * </ContentGroup>
 *
 * @example
 * // Subtle variant (background tint only)
 * <ContentGroup variant="subtle" bg="$bgSubtle">
 *   <VStack space="md">...</VStack>
 * </ContentGroup>
 *
 * @example
 * // Flush variant (spacing only, no visual treatment)
 * <ContentGroup variant="flush">
 *   <VStack space="md">...</VStack>
 * </ContentGroup>
 */
const ContentGroup: React.FC<ContentGroupProps> = ({
  children,
  variant = 'accent',
  accentColor = '$accent400',
  accentWidth = 3,
  bg,
  p = '$4',
  ...props
}) => {
  // Flush variant: no visual treatment, but pass through bg
  if (variant === 'flush') {
    return (
      <Box bg={bg} p={p} {...props}>
        {children}
      </Box>
    );
  }

  // Subtle variant: background tint only
  if (variant === 'subtle') {
    return (
      <Box bg={bg || '$bgSubtle'} p={p} {...props}>
        {children}
      </Box>
    );
  }

  // Bordered variant: full border + accent bar
  if (variant === 'bordered') {
    return (
      <Box
        flexDirection="row"
        bg={bg || '$bgCard'}
        borderWidth={1}
        borderColor="$borderLight"
        overflow="hidden"
        {...props}
      >
        <Box
          w={accentWidth}
          bg={accentColor}
          alignSelf="stretch"
          flexShrink={0}
        />
        <Box flex={1} p={p}>
          {children}
        </Box>
      </Box>
    );
  }

  // Accent variant (default): left accent bar only.
  // When `bg` is set (tinted group), apply full padding so the surface
  // matches Policies/LevelForm spacing instead of left-only inset.
  return (
    <Box
      borderLeftWidth={accentWidth}
      borderLeftColor={accentColor}
      {...(bg ? { p, bg } : { pl: p })}
      {...props}
    >
      {children}
    </Box>
  );
};

export default ContentGroup;
